import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Select2Component from '../CustomSelect/Select2';
import { getUserSegments } from '../../Action/UserSegments/UserSegments_Action';
import { usePermission } from '../../hooks/usePermission';
import { PERM } from '../../config/permissions';
import { SERIES_AUDIENCE_OPTIONS, formatSeriesAudience } from '../../utils/seriesAudience';

/**
 * CI-958 — who the series is live to. Changing it needs user_segments:edit;
 * without it the current audience is shown read-only.
 *
 * `currentSegments` ([{ id, name }] from the series details) keeps the selected
 * chips labelled before — or without — the segment list loading.
 */
export default function SeriesAudienceField({ audience, segmentIds, currentSegments = [], onChange, error, disabled }) {
    const { canView, canEdit } = usePermission();
    const allowEdit = canEdit(PERM.USER_SEGMENTS);
    const allowView = allowEdit || canView(PERM.USER_SEGMENTS);
    const dispatch = useDispatch();
    const [segments, setSegments] = useState([]);

    useEffect(() => {
        if (!allowView) return;
        dispatch(getUserSegments({ getAll: true }))
            .then(res => setSegments(Array.from(res?.responseDetails || [])))
            .catch(() => { /* the current selection still renders from currentSegments */ });
    }, [dispatch, allowView]);

    const options = useMemo(() => {
        const byId = new Map(currentSegments.map(s => [Number(s.id), s.name]));
        segments.forEach(s => byId.set(Number(s.id), s.name));
        return [...byId].map(([value, label]) => ({ value, label }));
    }, [segments, currentSegments]);

    if (!allowEdit) {
        return (
            <div className="form-group">
                <label className="form-label">Audience</label>
                <p className="mb-0">{formatSeriesAudience({ audience, audience_segments: currentSegments })}</p>
            </div>
        );
    }

    return (
        <div className="form-group">
            <label htmlFor="series_audience" className="form-label">Audience <span className="text-danger">*</span></label>
            <select id="series_audience" className="form-control" value={audience} disabled={disabled}
                onChange={e => onChange({ audience: e.target.value })} aria-describedby="series-audience-help">
                {SERIES_AUDIENCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {audience === 'SPECIFIC' && (
                <div className="mt-2">
                    <Select2Component
                        value={segmentIds}
                        options={options}
                        name="segment_ids"
                        placeholder="Select user segments"
                        onChange={value => onChange({ segment_ids: value })}
                        isDisabled={disabled}
                        isMulti
                    />
                </div>
            )}
            <small id="series-audience-help">
                {audience === 'SPECIFIC'
                    ? 'Only users in the selected segments can see this series (e.g. for QC). Everyone else never sees it.'
                    : 'Visible to everyone once the series is Active.'}
            </small>
            <span className="text-danger pt-2 d-block">{error}</span>
        </div>
    );
}
